import { useEffect, useState } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { cashCockpitApi } from '../../services/cashCockpitApi';
import type { ForecastBreakdownData, ForecastFlowType } from '../../types/cashCockpit';
import { formatMoney } from './charts/IbcsTooltip';

interface Props {
    entities: string[];
    weekStart: string;
    weekEnd: string;
    weekNumber: number;
    flowType: ForecastFlowType;
    currency: string;
    onClose: () => void;
}

const FLOW_TITLE: Record<ForecastFlowType, string> = {
    ar: 'AR Inflows — open receivables due this week',
    ap: 'AP Outflows — open payables due this week',
    plannedIn: 'Planned Inflows — planned cash events (incoming)',
    plannedOut: 'Planned Outflows — planned cash events (outgoing)',
};

const PARTY_LABEL: Record<ForecastFlowType, string> = {
    ar: 'Customer',
    ap: 'Vendor',
    plannedIn: 'Counterparty',
    plannedOut: 'Counterparty',
};

const REF_LABEL: Record<ForecastFlowType, string> = {
    ar: 'Document',
    ap: 'Document',
    plannedIn: 'Type',
    plannedOut: 'Type',
};

export default function ForecastBreakdownModal({
    entities, weekStart, weekEnd, weekNumber, flowType, currency, onClose,
}: Props) {
    const [data, setData] = useState<ForecastBreakdownData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setError(null);
            try {
                const res = await cashCockpitApi.forecastBreakdown(entities, weekStart, weekEnd, flowType, currency);
                if (!cancelled) setData(res);
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load details');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [entities, weekStart, weekEnd, flowType, currency]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const displayCurrency = data?.displayCurrency ?? (currency === 'ALL' ? 'EUR' : currency);
    const isAbsolute = flowType === 'ap' || flowType === 'plannedOut';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
            <div
                className="bg-white rounded-lg shadow-xl w-[92vw] max-w-5xl max-h-[82vh] flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">{FLOW_TITLE[flowType]}</h3>
                        <div className="text-xs text-gray-500 mt-0.5">
                            Week {weekNumber} · {weekStart} to {weekEnd} · {entities.join(', ').toUpperCase()} · values in {displayCurrency}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto">
                    {loading && (
                        <div className="flex items-center justify-center py-16 text-gray-500">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                        </div>
                    )}
                    {error && (
                        <div className="mx-6 my-4 flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded p-3 text-sm">
                            <AlertCircle className="w-4 h-4" /> {error}
                        </div>
                    )}
                    {!loading && !error && data && data.items.length === 0 && (
                        <div className="py-16 text-center text-gray-500 text-sm">No items for this bucket.</div>
                    )}
                    {!loading && !error && data && data.items.length > 0 && (
                        <table className="w-full text-xs">
                            <thead className="bg-gray-50 text-gray-600 sticky top-0">
                                <tr>
                                    <th className="px-3 py-2 text-left">Date</th>
                                    <th className="px-3 py-2 text-left">Company</th>
                                    <th className="px-3 py-2 text-left">{REF_LABEL[flowType]}</th>
                                    <th className="px-3 py-2 text-left">{PARTY_LABEL[flowType]}</th>
                                    <th className="px-3 py-2 text-left">Description</th>
                                    <th className="px-3 py-2 text-right">Amount (FCY)</th>
                                    <th className="px-3 py-2 text-left">CCY</th>
                                    <th className="px-3 py-2 text-right">Amount ({displayCurrency})</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((it, idx) => (
                                    <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                                        <td className="px-3 py-1.5 text-gray-600">{it.date}</td>
                                        <td className="px-3 py-1.5 uppercase font-medium">{it.companyCode}</td>
                                        <td className="px-3 py-1.5">
                                            <div className="font-medium">{it.reference}</div>
                                            {it.extra && <div className="text-[10px] text-gray-500">{it.extra}</div>}
                                        </td>
                                        <td className="px-3 py-1.5 text-gray-700">{it.party || '—'}</td>
                                        <td className="px-3 py-1.5 text-gray-700 max-w-xs truncate" title={it.description ?? ''}>
                                            {it.description || '—'}
                                        </td>
                                        <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                                            {it.amountFCY.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-3 py-1.5 text-gray-500">{it.currencyCode}</td>
                                        <td className={`px-3 py-1.5 text-right font-mono tabular-nums font-semibold ${
                                            it.amountInDisplayCurrency >= 0 ? 'text-[#2C7BB6]' : 'text-[#D7191C]'
                                        }`}>
                                            {formatMoney(isAbsolute ? Math.abs(it.amountInDisplayCurrency) : it.amountInDisplayCurrency, displayCurrency, false)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50 sticky bottom-0 border-t-2 border-gray-300">
                                <tr>
                                    <td colSpan={7} className="px-3 py-2 text-right font-semibold text-gray-700">
                                        Total ({data.items.length} item{data.items.length === 1 ? '' : 's'})
                                    </td>
                                    <td className={`px-3 py-2 text-right font-mono font-semibold ${
                                        data.total >= 0 ? 'text-[#2C7BB6]' : 'text-[#D7191C]'
                                    }`}>
                                        {formatMoney(isAbsolute ? Math.abs(data.total) : data.total, displayCurrency, false)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
